import React, { useMemo } from 'react';

const LollipopPlot = ({ gene, mutations, geneInfo, width = 800, height = 300 }) => {
  const info = geneInfo[gene];
  if (!info) return <div className="p-4 text-gray-500">No gene information available for {gene}</div>;

  const geneMutations = mutations.filter(m => m.gene === gene && m.proteinPosition);

  const margin = { top: 60, right: 40, bottom: 60, left: 60 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;

  const proteinLength = info.length;
  const scale = plotWidth / proteinLength;

  // Group mutations by position
  const mutationGroups = useMemo(() => {
    const groups = {};
    geneMutations.forEach(m => {
      const pos = m.proteinPosition;
      if (!groups[pos]) groups[pos] = [];
      groups[pos].push(m);
    });
    return groups;
  }, [geneMutations]);

  // Calculate max count for scaling lollipop heights
  const maxCount = Math.max(...Object.values(mutationGroups).map(g => g.length), 1);

  // Color scheme based on consequence type
  const getColor = (consequence) => {
    const colors = {
      missense_variant: '#e74c3c',
      stop_gained: '#2c3e50',
      frameshift_variant: '#9b59b6',
      inframe_deletion: '#f39c12',
      inframe_insertion: '#e67e22',
      splice_donor_variant: '#1abc9c',
      splice_acceptor_variant: '#1abc9c',
      default: '#7f8c8d'
    };
    return colors[consequence] || colors.default;
  };

  // Domain colors
  const domainColors = ['#3498db', '#2ecc71', '#e74c3c', '#f39c12', '#9b59b6', '#1abc9c', '#34495e'];

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-semibold mb-2">{gene} - {info.name}</h3>
      <p className="text-sm text-gray-600 mb-4">Protein length: {proteinLength} amino acids | {geneMutations.length} mutations shown</p>

      <svg width={width} height={height} className="overflow-visible">
        <defs>
          {/* Gradient for protein backbone */}
          <linearGradient id="proteinGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#bdc3c7" />
            <stop offset="100%" stopColor="#95a5a6" />
          </linearGradient>
        </defs>

        <g transform={`translate(${margin.left}, ${margin.top})`}>
          {/* Protein backbone */}
          <rect
            x={0}
            y={plotHeight / 2 - 15}
            width={plotWidth}
            height={30}
            fill="url(#proteinGradient)"
            rx={5}
          />

          {/* Protein domains */}
          {info.domains && info.domains.map((domain, i) => (
            <g key={domain.name}>
              <rect
                x={domain.start * scale}
                y={plotHeight / 2 - 20}
                width={(domain.end - domain.start) * scale}
                height={40}
                fill={domainColors[i % domainColors.length]}
                opacity={0.8}
                rx={3}
              />
              <text
                x={(domain.start + domain.end) / 2 * scale}
                y={plotHeight / 2 + 5}
                textAnchor="middle"
                fill="white"
                fontSize="10"
                fontWeight="bold"
              >
                {domain.name}
              </text>
            </g>
          ))}

          {/* Lollipops */}
          {Object.entries(mutationGroups).map(([pos, muts]) => {
            const x = pos * scale;
            const height_val = 30 + (muts.length / maxCount) * (plotHeight / 2 - 40);
            const primaryMut = muts[0];

            return (
              <g key={pos} className="cursor-pointer hover:opacity-80">
                {/* Stick */}
                <line
                  x1={x}
                  y1={plotHeight / 2 - 20}
                  x2={x}
                  y2={plotHeight / 2 - 20 - height_val}
                  stroke={getColor(primaryMut.consequence)}
                  strokeWidth={2}
                />
                {/* Circle */}
                <circle
                  cx={x}
                  cy={plotHeight / 2 - 20 - height_val}
                  r={Math.min(6 + muts.length * 2, 15)}
                  fill={getColor(primaryMut.consequence)}
                  stroke="white"
                  strokeWidth={2}
                />
                {/* Count label if multiple */}
                {muts.length > 1 && (
                  <text
                    x={x}
                    y={plotHeight / 2 - 20 - height_val + 4}
                    textAnchor="middle"
                    fill="white"
                    fontSize="10"
                    fontWeight="bold"
                  >
                    {muts.length}
                  </text>
                )}
                {/* Position label */}
                <text
                  x={x}
                  y={plotHeight / 2 - 20 - height_val - 10}
                  textAnchor="middle"
                  fill="#2c3e50"
                  fontSize="9"
                >
                  {primaryMut.hgvsp?.split('.')[1] || `p.${pos}`}
                </text>
                {/* Tooltip */}
                <title>
                  {muts.map(m => `${m.hgvsp} (${m.consequence})`).join('\n')}
                </title>
              </g>
            );
          })}

          {/* X-axis */}
          <line
            x1={0}
            y1={plotHeight / 2 + 30}
            x2={plotWidth}
            y2={plotHeight / 2 + 30}
            stroke="#2c3e50"
            strokeWidth={1}
          />

          {/* X-axis ticks */}
          {[0, 0.25, 0.5, 0.75, 1].map(frac => {
            const pos = Math.round(frac * proteinLength);
            return (
              <g key={frac}>
                <line
                  x1={frac * plotWidth}
                  y1={plotHeight / 2 + 30}
                  x2={frac * plotWidth}
                  y2={plotHeight / 2 + 35}
                  stroke="#2c3e50"
                />
                <text
                  x={frac * plotWidth}
                  y={plotHeight / 2 + 48}
                  textAnchor="middle"
                  fill="#2c3e50"
                  fontSize="11"
                >
                  {pos}
                </text>
              </g>
            );
          })}

          {/* X-axis label */}
          <text
            x={plotWidth / 2}
            y={plotHeight / 2 + 65}
            textAnchor="middle"
            fill="#2c3e50"
            fontSize="12"
          >
            Amino Acid Position
          </text>
        </g>

        {/* Legend */}
        <g transform={`translate(${width - 150}, 10)`}>
          <text x={0} y={0} fontSize="11" fontWeight="bold" fill="#2c3e50">Mutation Types</text>
          {[
            { label: 'Missense', color: '#e74c3c' },
            { label: 'Nonsense', color: '#2c3e50' },
            { label: 'Frameshift', color: '#9b59b6' },
            { label: 'In-frame', color: '#f39c12' },
            { label: 'Splice', color: '#1abc9c' }
          ].map((item, i) => (
            <g key={item.label} transform={`translate(0, ${15 + i * 15})`}>
              <circle cx={6} cy={0} r={5} fill={item.color} />
              <text x={16} y={4} fontSize="10" fill="#2c3e50">{item.label}</text>
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
};

export default LollipopPlot;
